import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ArrowRight, Loader2, MailCheck, KeyRound } from "lucide-react";

/**
 * One door for "I forgot my password" and "I was never asked to set one".
 * The backend works out which link the person needs. Always reports the same
 * outcome so nobody can probe for valid partner emails.
 */
export function AccessLinkForm({ kind = "introducer", defaultEmail = "", onBack, testid = "access" }) {
  const [email, setEmail] = useState(defaultEmail);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try { await api.post("/portal/access-link", { email: email.trim() }); } catch { /* same outcome either way */ }
    setBusy(false);
    setSent(true);
  };

  if (sent) {
    return (
      <div className="mt-5" data-testid={`${testid}-sent`}>
        <div className="rounded-2xl border border-teal-400/25 bg-teal-500/[0.07] p-4 flex items-start gap-3">
          <span className="grid place-items-center size-8 rounded-xl bg-teal-500/15 border border-teal-400/30 text-teal-300 shrink-0">
            <MailCheck className="size-4" />
          </span>
          <div>
            <div className="text-[13.5px] font-semibold text-white">Check your inbox</div>
            <p className="mt-1 text-[12.5px] text-slate-300 leading-relaxed">
              If <span className="text-white">{email}</span> is on our partner list, a link is on its way — it lets you
              set a password and get straight in. We've texted it too if we have your mobile.
            </p>
            <p className="mt-2 text-[11.5px] text-slate-500 leading-relaxed">
              Nothing after a couple of minutes? Check spam, or email{" "}
              <a href="mailto:ops@connex.is" className="text-[#7da9ff] underline underline-offset-2">ops@connex.is</a> and we'll sort it.
            </p>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <button type="button" onClick={() => setSent(false)}
                  className="text-[12px] text-slate-400 hover:text-white transition-colors" data-testid={`${testid}-try-again`}>
            Try a different email
          </button>
          {onBack && (
            <button type="button" onClick={onBack}
                    className="inline-flex items-center gap-1.5 text-[12px] text-slate-300 hover:text-white transition-colors"
                    data-testid={`${testid}-back`}>
              <ArrowLeft className="size-3" /> Back to sign in
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-5" data-testid={`${testid}-recover-form`}>
      <Label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-400 font-semibold">Your email</Label>
      <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
             className="mt-1.5 h-11 bg-white/[0.04] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-[#0055FF]/40 focus-visible:border-[#0055FF]/50"
             placeholder="the email we invited" data-testid={`${testid}-recover-email`} autoFocus />
      <Button type="submit" disabled={busy}
              className="mt-4 w-full h-11 bg-[#0055FF] hover:bg-[#0044CC] rounded-xl font-semibold text-[14px] shadow-lg shadow-[#0055FF]/25 transition-all active:scale-[0.99]"
              data-testid={`${testid}-recover-submit`}>
        {busy ? <Loader2 className="size-4 animate-spin" /> : <><KeyRound className="size-4 mr-1.5" /> Email me a link <ArrowRight className="size-4 ml-1" /></>}
      </Button>
      {onBack && (
        <button type="button" onClick={onBack}
                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-[12px] text-slate-400 hover:text-white transition-colors"
                data-testid={`${testid}-back`}>
          <ArrowLeft className="size-3" /> Back to sign in
        </button>
      )}
    </form>
  );
}
